// 채팅 메시지 전송/재생성 스트리밍 엔진 — SSE(meta·token·sources·status·file·done) + 포그라운드/백그라운드
// 뷰 분리(viewTokenRef)·rAF 타이핑(streamer)·응답중지(abort)·'작성 중' 표시(genConvIds)·문서생성/코워크 상태.
// ⚠️stale-closure 가드(viewTokenRef/currentConvIdRef)와 실행 순서는 원본 그대로 유지(순수 리팩터). 의존은 주입.
import { useState, useEffect, useRef } from 'react';
import { chatAPI, historyAPI } from '../../../api/client';
import { createSmoothStreamer } from '../../../utils/smoothStreamer';
import { confirmDialog } from '../../../components/AppDialog';
import { normalizeConversationLoad } from './useConversation';

// 도구 호출 진행 표시 — 서버가 보내는 tool_pending 의 tool_name 을 사람 말로 바꾼다.
// 목록에 없는 도구(관리자가 등록한 커스텀 SQL 도구 등)는 기본 문구로 떨어진다.
const TOOL_LABELS = {
  web_search: '웹 검색 중',
  create_document: '문서 만드는 중',
  create_spreadsheet: '엑셀 만드는 중',
  create_presentation: 'PPT 만드는 중',
  list_workspace_files: '작업공간 파일 목록 보는 중',
  read_workspace_file: '파일 읽는 중',
  write_workspace_file: '파일 저장하는 중',
  edit_workspace_file: '파일 수정하는 중',
  analyze_spreadsheet: '표 분석하는 중',
  aggregate_spreadsheet: '표 집계하는 중',
  list_db_tables: 'DB 테이블 보는 중',
  describe_db_table: 'DB 구조 보는 중',
  run_db_select: '사내 데이터 조회 중',
  find_pwn: '화주 찾는 중',
  find_depot: '센터 찾는 중',
  search_product_stock: '상품 재고 조회 중',
  get_order_status: '주문 조회 중',
  get_order_summary_by_pwn: '주문 집계 중',
  get_channel_orders_by_pwn: '판매채널별 주문 조회 중',
  get_shipment_summary: '출고 조회 중',
  get_shipment_trend: '출고 추이 조회 중',
  get_shipment_by_pwn: '화주별 출고 조회 중',
  get_shipment_by_depot: '센터별 출고 조회 중',
  get_stock_by_pwn: '화주별 재고 조회 중',
  get_stock_by_depot: '센터별 재고 조회 중',
  get_top_products_by_shipment: '출고 상위품목 조회 중',
  list_products_by_pwn: '취급상품 조회 중',
};

const generatedFileMarker = (file) => {
  if (!file?.file_id || !file?.filename) return '';
  const legacy = `[[doc:${file.file_id}:${file.filename}]]`;
  if (!file.mime_type || file.size == null || !file.format) return legacy;
  return `${legacy}[[doc:${file.file_id}:${file.filename}|mime=${file.mime_type}|size=${file.size}|format=${file.format}]]`;
};

const reuseSelections = new Map();
const reuseListeners = new Set();

const reuseKey = (convId) => {
  if (convId === null || convId === undefined || convId === '') return null;
  return String(convId);
};

const normalizeReuseFiles = (files) => {
  const seen = new Set();
  return (Array.isArray(files) ? files : [])
    .map((file) => ({
      id: Number(file?.id ?? file?.file_id),
      name: file?.name || file?.original_name || '첨부 파일',
      expired: !!file?.expired,
    }))
    .filter((file) => Number.isFinite(file.id) && !file.expired)
    .filter((file) => {
      if (seen.has(file.id)) return false;
      seen.add(file.id);
      return true;
    });
};

const emitReuseSelection = (key, files) => {
  reuseListeners.forEach((listener) => {
    try { listener(key, files); } catch {}
  });
};

export const getAttachmentReuseFiles = (convId) => {
  const key = reuseKey(convId);
  if (!key) return [];
  return [...(reuseSelections.get(key) || [])];
};

export const setAttachmentReuseFiles = (convId, files) => {
  const key = reuseKey(convId);
  if (!key) return [];
  const next = normalizeReuseFiles(files);
  if (next.length) reuseSelections.set(key, next);
  else reuseSelections.delete(key);
  emitReuseSelection(key, [...next]);
  return next;
};

export const clearAttachmentReuseFiles = (convId) => {
  const key = reuseKey(convId);
  if (!key) return;
  const hadSelection = reuseSelections.delete(key);
  if (hadSelection) emitReuseSelection(key, []);
};

export const clearAllAttachmentReuseFiles = () => {
  const keys = [...reuseSelections.keys()];
  reuseSelections.clear();
  keys.forEach((key) => emitReuseSelection(key, []));
};

export const removeAttachmentReuseFileIds = (convId, fileIds) => {
  const removeIds = new Set((Array.isArray(fileIds) ? fileIds : []).map((id) => Number(id)).filter(Number.isFinite));
  if (!removeIds.size) return;
  const current = getAttachmentReuseFiles(convId);
  setAttachmentReuseFiles(convId, current.filter((file) => !removeIds.has(Number(file.id))));
};

export const subscribeAttachmentReuseFiles = (listener) => {
  reuseListeners.add(listener);
  return () => reuseListeners.delete(listener);
};

const replaceMessagesFromHistory = (setMessages, data, convId, transform = (items) => items) => {
  const loaded = normalizeConversationLoad(data, convId).messages;
  if (loaded.length === 0) return;
  setMessages(transform(loaded));
};

const terminalStatuses = new Set(['failed', 'exhausted', 'paused', 'cancelled']);

const isStructuredTerminalError = (data) => (
  terminalStatuses.has(data?.status)
  || terminalStatuses.has(data?.terminal_reason)
  || Boolean(data?.stop_reason && data?.terminal_reason)
);

const terminalStatusMessage = (data) => {
  const reason = data?.terminal_reason || data?.stop_reason || data?.status;
  if (reason === 'max_tokens') return 'AI 응답이 길이 제한으로 잘렸습니다. 이어서 질문하거나 다시 시도해 주세요.';
  if (reason === 'pause_turn') return 'AI 응답이 일시 중지되었습니다. 다시 시도해 주세요.';
  if (reason === 'client_abort' || data?.status === 'cancelled') return 'AI 응답이 중지되었습니다.';
  if (data?.status === 'exhausted') return 'AI 응답 반복 한도에 도달했습니다. 다시 시도해 주세요.';
  return 'AI 응답이 완료되지 않았습니다. 다시 시도해 주세요.';
};

export default function useChatMessage({
  // core
  conversations, setConversations, currentConvId, setCurrentConvId, messages, setMessages, currentConvIdRef,
  selectedProjectId, currentProjectAccessRole = null, cowork,
  // features
  webSearch, reasoning, selectedChatModel, internalData, useRag, setRagSources, refreshMyUsage,
  // attachments
  pendingFiles, setPendingFiles,
  pendingFolders = [], setPendingFolders = () => {},   // 드라이브 폴더 첨부 { path, name, file_count, truncated }
  // workspace
  loadWorkspaceFiles, setWorkspaceBusy, skillIdRef,
  // limits
  setLimitModal, setLimitReason, setLimitRequested, setWebLimitModal, setWebLimitRequested,
  // ui scroll
  atBottomRef, setShowScrollDown, scrollRef,
  setChatError = () => {},
}) {
  const [input, setInput]                 = useState('');
  const [loading, setLoading]             = useState(false);
  // setInterval 콜백은 클로저에 갇혀 옛 loading 을 본다 -> ref 로 최신값을 따로 들고 간다.
  const loadingRef = useRef(false);
  const [generatingDoc, setGeneratingDoc] = useState(false);   // 문서 생성 중 표시
  const [genConvIds, setGenConvIds]       = useState(() => new Set());   // 현재 응답 작성 중인 대화 id 집합(백그라운드 포함)
  // 🔴 409(RUN_IN_PROGRESS) 로 막힌 대화. 백그라운드 생성은 연결이 끊겨도 계속 돌기 때문에
  //    그 대화가 **최대 20분** 잠길 수 있다(dev 실측: 끊고 나서도 56초간 409).
  //    문구만 띄우면 사용자는 빠져나갈 길이 없다 -> '중지하고 새로 묻기' 를 붙인다.
  const [blockedConvId, setBlockedConvId] = useState(null);
  const textareaRef = useRef(null);
  const abortRef    = useRef(null);   // 응답 중지용 AbortController
  const streamerRef = useRef(null);   // 스트리밍 텍스트 부드러운 표시(rAF 타이핑) 컨트롤러
  const viewTokenRef = useRef(0);     // 포그라운드 뷰 소유권 토큰 — 증가하면 진행 중 스트림은 백그라운드로 분리(화면 안 씀)

  const normalizeErrorDetail = (detail) => (
    detail && typeof detail === 'object' && !detail.code && detail.detail && typeof detail.detail === 'object'
      ? detail.detail
      : detail
  );

  const mapHttpError = (status, rawDetail) => {
    const detail = normalizeErrorDetail(rawDetail);
    if (detail?.code === 'RUN_IN_PROGRESS') {
      // 같은 대화에 두 생성이 겹치면 부분 답변이 다른 질문의 답으로 섞인다. 서버가 막는다.
      return detail.message || '이전 답변을 만들고 있습니다. 중지한 뒤 다시 보내 주세요.';
    }
    if (detail?.code === 'ATTACHMENT_UNAVAILABLE') {
      const reasonLabel = {
        not_found: '삭제됨',
        expired: '만료됨',
        missing_original: '원본 없음',
        unsupported_mime: '지원하지 않는 형식',
        content_block_failed: '원문 변환 실패',
      };
      const files = Array.isArray(detail.files) ? detail.files : [];
      const fileText = files
        .map((file) => {
          const label = file?.name || (file?.id !== undefined && file?.id !== null ? `파일 ${file.id}` : '첨부 파일');
          return `${label} (${reasonLabel[file?.reason] || file?.reason || '사용 불가'})`;
        })
        .join(', ');
      return fileText
        ? `첨부 파일 원문을 다시 사용할 수 없습니다: ${fileText}`
        : (detail.message || '첨부 파일 원문을 다시 사용할 수 없습니다.');
    }
    if (detail?.code === 'SIDE_EFFECT_RERUN_REQUIRES_BRANCH_ENDPOINT') {
      return detail.message || '생성 파일 또는 작업공간 변경이 있는 턴은 별도 분기 실행이 필요합니다.';
    }
    if (detail?.code === 'TURN_TOOL_UNAVAILABLE') {
      return detail.message || detail.reason || '원본 턴의 도구를 현재 사용할 수 없어 재생성할 수 없습니다.';
    }
    if (detail?.code === 'TURN_MODEL_UNAVAILABLE') {
      const model = detail.model ? ` (${detail.model})` : '';
      return detail.message || `원본 턴의 모델${model}을 현재 사용할 수 없어 재생성할 수 없습니다.`;
    }
    if (status === 413) return '입력이 너무 큽니다. 내용을 줄여 다시 보내주세요.';
    if (status === 401 || status === 403) return '로그인이 만료되었습니다. 다시 로그인해 주세요.';
    if (status === 400) return typeof detail === 'string' ? detail : '요청을 처리할 수 없습니다. 입력을 확인해 주세요.';
    if (status >= 500 || status === 502 || status === 503) return '서버 오류입니다. 잠시 후 다시 시도해 주세요.';
    return typeof detail === 'string' ? detail : '오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  };

  const showStreamLimit = (data) => {
    const detail = data?.detail && typeof data.detail === 'object'
      ? { ...data.detail, code: data.code || data.detail.code }
      : data;
    if (detail?.code === 'LIMIT_EXCEEDED') {
      setLimitModal({ used: detail.used, limit: detail.limit });
      setLimitReason('');
      chatAPI.getLimitRequestStatus().then((r) => setLimitRequested(r.data.has_pending)).catch(() => setLimitRequested(false));
      return true;
    }
    if (detail?.code === 'WEB_SEARCH_LIMIT_EXCEEDED') {
      setWebLimitModal({ used: detail.used, limit: detail.limit });
      chatAPI.getLimitRequestStatus('web_search').then((r) => setWebLimitRequested(r.data.has_pending)).catch(() => setWebLimitRequested(false));
      return true;
    }
    return false;
  };

  const restoreInput = (value) => {
    setInput(value);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    });
  };

  const removeFailedTurn = (prev, alsoRemoveAssistant = false) => {
    const msgs = [...prev];
    if (alsoRemoveAssistant) {
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i]?.role === 'assistant') {
          msgs.splice(i, 1);
          break;
        }
      }
    }
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i]?.role === 'user') {
        msgs.splice(i, 1);
        break;
      }
    }
    return msgs;
  };

  const removeTrailingEmptyAssistant = (prev) => {
    const msgs = [...prev];
    const last = msgs[msgs.length - 1];
    if (last?.role === 'assistant' && !last.content) msgs.pop();
    return msgs;
  };

  const withEphemeralThinking = (list, thinking) => {
    if (!thinking) return list;
    const msgs = [...(list || [])];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i]?.role === 'assistant') {
        msgs[i] = { ...msgs[i], thinking };
        break;
      }
    }
    return msgs;
  };

  const citationKey = (citation) => JSON.stringify([
    citation?.source_type || null,
    citation?.title || null,
    citation?.url || null,
    citation?.document_id ?? null,
    citation?.chunk_id ?? null,
    citation?.cited_text || null,
    citation?.location_type || null,
    citation?.start ?? null,
    citation?.end ?? null,
    citation?.page ?? null,
    citation?.sheet || null,
    citation?.slide ?? null,
  ]);

  const normalizeCitation = (citation, sequence) => ({
    schema_version: citation?.schema_version || 1,
    source_type: citation?.source_type ?? null,
    title: citation?.title ?? null,
    url: citation?.url ?? null,
    document_id: citation?.document_id ?? null,
    chunk_id: citation?.chunk_id ?? null,
    cited_text: citation?.cited_text ?? '',
    location_type: citation?.location_type ?? null,
    start: citation?.start ?? null,
    end: citation?.end ?? null,
    page: citation?.page ?? null,
    sheet: citation?.sheet ?? null,
    slide: citation?.slide ?? null,
    sequence: citation?.sequence ?? sequence,
    partial: !!citation?.partial,
    ...(citation?.source_ref ? { source_ref: citation.source_ref } : {}),
  });

  const mergeCitations = (current, incoming) => {
    const out = Array.isArray(current) ? current.map((c, idx) => normalizeCitation(c, idx + 1)) : [];
    const seen = new Set(out.map(citationKey));
    (Array.isArray(incoming) ? incoming : [incoming]).forEach((citation) => {
      if (!citation?.cited_text) return;
      const normalized = normalizeCitation(citation, out.length + 1);
      const key = citationKey(normalized);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(normalized);
    });
    return out;
  };

  const updateLastAssistantCitations = (incoming, { replace = false, isForeground = () => true } = {}) => {
    if (!isForeground()) return;
    setMessages((prev) => {
      const msgs = [...prev];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i]?.role !== 'assistant') continue;
        msgs[i] = {
          ...msgs[i],
          citations: replace
            ? mergeCitations([], incoming)
            : mergeCitations(msgs[i].citations, incoming),
        };
        break;
      }
      return msgs;
    });
  };

  // 맨 아래 근처일 때만 자동 추적(사용자가 위로 올려 읽는 중이면 끌어내리지 않음)
  useEffect(() => {
    const el = scrollRef.current;
    if (el && atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [messages]);

  // 언마운트(다른 페이지로 이동 등) 시 진행 중 스트림/타이핑 루프 정리 — rAF·fetch 누수 방지
  useEffect(() => () => {
    try { abortRef.current?.abort(); } catch {}
    try { streamerRef.current?.flushNow(); } catch {}
    clearAllAttachmentReuseFiles();
  }, []);

  // ── 스트림 제어: 백그라운드 분리 · 새대화/전환 시 정리 ──
  const detachStream = () => {   // 진행 중 스트림을 백그라운드로 — abort 안 함(백엔드가 끝까지 생성·저장), UI만 분리
    viewTokenRef.current++;       // 포그라운드 토큰 무효화 → 실행 중 스트림은 더이상 화면에 안 씀
    streamerRef.current = null;   // 정지버튼/flush 대상에서 제외(백그라운드 스트림 로컬 streamer는 콜백이 fg()로 차단됨)
    setLoading(false);            // 새 화면은 즉시 입력 가능(백그라운드 스트림과 무관)
    // 🔴 진행 표시도 **여기서** 끈다(2026-08-24 마스터 신고).
    //    분리된 스트림의 정리 코드는 전부 `fg()` 안에 있어서, 분리된 뒤에는 **아무도 이걸 안 끈다.**
    //    그래서 PPT 생성 중에 '새 대화'를 열면 "발표자료 만드는 중 —…" 문구가 새 대화로 따라와
    //    남아 있었다(그 대화는 아무것도 안 만드는데). 대화를 옮길 때 함께 지운다.
    setGeneratingDoc(false);
    setWorkspaceBusy(false);
    setChatError(null);           // 앞 대화에서 난 오류가 새 화면에 남지 않게(ChatPage 는 null 초기값)
  };

  // 🔴 대화별로 보관한다. 인스턴스당 하나만 두면 A 생성 중 B 로 갔다 A 로 돌아와
  //    중지를 누를 때 **B 의 run 을 죽인다**.
  const runIdsRef = useRef(new Map());
  // stopBlockingRunAndRetry 가 handleSend 보다 위에 있어 직접 못 부른다 -> ref 로 잇는다.
  const handleSendRef = useRef(() => {});

  const stopResponse = () => {
    // 🔴 순서가 중요하다. 이제 연결을 끊는 것만으로는 서버가 안 멈춘다
    //    (백그라운드 생성이 켜져 있으면 끝까지 만들고 저장한다 = 과금된다).
    //    그래서 stop 을 **먼저** 보내고 abort 는 그다음이다.
    const rid = runIdsRef.current.get(currentConvIdRef.current);
    const done = () => { if (abortRef.current) abortRef.current.abort(); };
    if (!rid) { done(); return; }
    chatAPI.stopRun(rid)
      .catch(() => {
        // 중지 요청이 못 갔다 = 서버는 계속 만든다. 사용자에게 알린다 —
        // 조용히 넘기면 '중지를 눌렀는데 과금되고 답도 온다'가 된다.
        setChatError('중지 요청을 보내지 못했습니다. 답변이 서버에서 완료될 수 있습니다.');
      })
      .finally(done);
  };

  // 409 로 막혔을 때의 탈출구. 중지시키고, 서버가 정말 멈출 때까지 잠깐 기다렸다 다시 보낸다.
  // (중지는 감시 주기 때문에 즉시가 아니다 — 바로 다시 보내면 또 409 다.)
  const stopBlockingRunAndRetry = async () => {
    const convId = blockedConvId ?? currentConvIdRef.current;
    if (!convId) { setBlockedConvId(null); return; }
    try {
      const r = await chatAPI.listRuns(convId);
      const run = (r?.data?.running || [])[0];
      if (run?.run_id) await chatAPI.stopRun(run.run_id);
    } catch {
      setChatError('중지 요청을 보내지 못했습니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    for (let i = 0; i < 10; i++) {                 // 최대 ~10초
      await new Promise((r) => setTimeout(r, 1000));
      try {
        const r2 = await chatAPI.listRuns(convId);
        if (!(r2?.data?.running || []).length) {
          setBlockedConvId(null);
          setChatError(null);
          await handleSendRef.current();           // 입력창엔 글이 그대로 있다(failTurn 이 되돌려 놨다)
          return;
        }
      } catch { /* 다음 번에 다시 본다 */ }
    }
    setChatError('아직 이전 답변을 정리하는 중입니다. 잠시 후 다시 보내 주세요.');
  };

  // ── '응답 작성 중' 표시 추적(genConvIds) ──
  useEffect(() => { loadingRef.current = loading; }, [loading]);

  const markGen = (id) => { if (id) setGenConvIds((prev) => { if (prev.has(id)) return prev; const n = new Set(prev); n.add(id); return n; }); };
  const unmarkGen = (id) => { if (id) setGenConvIds((prev) => { if (!prev.has(id)) return prev; const n = new Set(prev); n.delete(id); return n; }); };

  // ── 복귀 시 "아직 만들고 있나" 확인 ─────────────────────────────────────────
  // 🔴 이게 원래 신고의 마지막 조각이다. 폰에서 앱을 나가면 스트림이 끊기는데, 서버는
  //    (백그라운드 생성이 켜져 있으면) 계속 만들어 저장한다. 그런데 돌아온 화면은 그걸 모른다 —
  //    답이 이미 서버에 있어도 대화를 다시 열기 전까지 안 보인다.
  //    그래서 화면이 다시 보일 때 서버에 물어보고, 만드는 중이면 끝날 때까지 지켜본다.
  const watchRef = useRef(null);

  const stopWatch = () => {
    if (watchRef.current) { clearInterval(watchRef.current); watchRef.current = null; }
  };

  const checkRunningOnReturn = async () => {
    const convId = currentConvIdRef.current;
    if (!convId || typeof convId !== 'number') return;
    if (loadingRef.current) return;          // 지금 내 화면이 스트리밍 중이면 그쪽이 주체다
    let running = false;
    try {
      const r = await chatAPI.listRuns(convId);
      running = !!(r?.data?.running || []).length;
    } catch {
      return;                                 // 서버가 모르면(꺼짐·구버전) 조용히 넘어간다
    }
    if (!running) return;
    markGen(convId);                          // 사이드바 '작성 중' 표시를 그대로 재사용한다
    stopWatch();
    watchRef.current = setInterval(async () => {
      const cur = currentConvIdRef.current;
      if (cur !== convId) { stopWatch(); unmarkGen(convId); return; }
      let still = false;
      try {
        const r = await chatAPI.listRuns(convId);
        still = !!(r?.data?.running || []).length;
      } catch {
        stopWatch(); unmarkGen(convId); return;
      }
      if (still) return;
      stopWatch();
      unmarkGen(convId);
      try {
        const h = await historyAPI.getMessages(convId);
        // 다른 대화로 옮겼거나 새 스트림이 시작됐으면 덮지 않는다.
        if (currentConvIdRef.current === convId && !loadingRef.current) {
          replaceMessagesFromHistory(setMessages, h.data, convId);
        }
      } catch { /* 재조회 실패는 조용히 — 다음 복귀에 다시 시도된다 */ }
    }, 3000);
  };

  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') checkRunningOnReturn(); };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onVis);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onVis);
      stopWatch();
    };
    // eslint-disable-next-line
  }, []);

  // ── 메시지 전송 + SSE 스트리밍(meta·token·status·file·done) ──
  const handleSend = async (streamOptions = {}) => {
    const replayLastUser = !!streamOptions.replayLastUser;
    const sourceInput = streamOptions.message ?? input;
    if (loading) return;
    const convIdAtStart = streamOptions.conversationId ?? currentConvId;
    const explicitReuseFiles = replayLastUser ? [] : getAttachmentReuseFiles(convIdAtStart);
    if (!sourceInput.trim() && pendingFiles.length === 0 && pendingFolders.length === 0 && explicitReuseFiles.length === 0 && !replayLastUser) return;   // 텍스트 없어도 첨부(파일·드라이브 폴더)가 있으면 전송 가능
    if (replayLastUser && !streamOptions.conversationId) return;
    if (streamerRef.current) { streamerRef.current.flushNow(); streamerRef.current = null; }
    atBottomRef.current = true; setShowScrollDown(false);   // 새 메시지는 맨 아래를 따라감
    const text = sourceInput.trim();
    const projectIdAtStart = selectedProjectId || null;
    let streamConvId = convIdAtStart;   // meta의 conv_id(함수 스코프 — finally가 봐서 '작성 중' 해제가 새 대화서도 동작)
    let streamProjectId = projectIdAtStart;
    let streamAccessRole = currentProjectAccessRole;

    // 대화 보존 정책: 최대 50개. 새 대화를 시작해 초과되면 생성일 오래된 것부터 자동 삭제(확인 후).
    const MAX_CONVS = 50;
    if (!replayLastUser && !convIdAtStart) {
      const activeConvs = conversations.filter((c) => !c.is_archived);   // 보관함 대화는 50캡·자동삭제 제외
      if (activeConvs.length >= MAX_CONVS) {
        const overflow = activeConvs.length - (MAX_CONVS - 1);   // 새 대화 추가 후 50개 유지에 필요한 삭제 수
        if (!(await confirmDialog(`대화는 최대 ${MAX_CONVS}개까지만 보관됩니다. (보관함 대화는 제외)\n새 대화를 시작하면 생성일이 가장 오래된 대화 ${overflow}개가 삭제됩니다. (즐겨찾기는 가능한 한 보존)\n계속하시겠습니까?`))) return;
        const byOldest = (a, b) => (new Date(a.created_at || 0) - new Date(b.created_at || 0)) || (a.id - b.id);
        let victims = activeConvs.filter((c) => !c.is_favorite).sort(byOldest).slice(0, overflow);
        if (victims.length < overflow) {   // 비즐겨찾기로 부족하면 즐겨찾기도 오래된 것부터
          victims = victims.concat(activeConvs.filter((c) => c.is_favorite).sort(byOldest).slice(0, overflow - victims.length));
        }
        for (const c of victims) { try { await historyAPI.deleteConversation(c.id); } catch { /* skip */ } }
        const delIds = new Set(victims.map((c) => c.id));
        setConversations((prev) => prev.filter((c) => !delIds.has(c.id)));
      }
    }

    if (!replayLastUser) {
      setInput('');
      if (textareaRef.current) textareaRef.current.style.height = 'auto';
    }
    setChatError(null);
    setLoading(true);
    setRagSources(null);   // 새 턴 시작 → 이전 답변의 참고문서 카드 제거

    const sentFiles = replayLastUser ? [] : [
      ...pendingFiles.map((f) => ({ id: f.file_id, name: f.name, expired: false })),
      ...explicitReuseFiles.map((f) => ({ id: f.id, name: f.name, expired: false, reused: true })),
    ];   // 내 메시지에 첨부 표시
    const sentFolders = replayLastUser ? [] : pendingFolders.map((f) => ({ path: f.path, name: f.name, file_count: f.file_count, truncated: !!f.truncated }));
    const sentText = text || (sentFiles.length || sentFolders.length ? '(첨부 파일)' : '');   // 첨부만 보낼 때 표시용(백엔드도 동일 플레이스홀더 저장)
    if (!replayLastUser) {
      setMessages((prev) => [...prev, {
        role: 'user', content: sentText, created_at: new Date().toISOString(), files: sentFiles,
        ...(sentFolders.length ? { drive_folders: sentFolders } : {}),   // 내 메시지에 폴더 칩 표시
      }]);
    }
    const fileIds = replayLastUser ? [] : [...new Set([
      ...pendingFiles.map((f) => f.file_id),
      ...explicitReuseFiles.map((f) => f.id),
    ])];
    const driveFolderPaths = sentFolders.map((f) => f.path);
    if (!replayLastUser) {
      setPendingFiles([]);
      setPendingFolders([]);
    }
    if (!replayLastUser && explicitReuseFiles.length) clearAttachmentReuseFiles(convIdAtStart);

    const controller = new AbortController();
    abortRef.current = controller;
    const myToken = ++viewTokenRef.current;   // 이 전송이 포그라운드 뷰를 소유
    const fg = () => viewTokenRef.current === myToken;   // 사용자가 아직 이 스트림 화면을 보는 동안 true
    if (convIdAtStart) markGen(convIdAtStart);   // 기존 대화면 즉시 '작성 중' 표시(새 대화는 meta에서)

    let assistantAdded = false;
    let receivedToken = false;
    let failedBeforeToken = false;
    let persistedTerminalBeforeToken = false;
    let specificStreamErrorShown = false;
    let thinkingText = '';
    const appendThinking = (textChunk) => {
      if (!textChunk) return;
      thinkingText += textChunk;
      if (!fg()) return;
      setMessages((prev) => {
        const msgs = [...prev];
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant') msgs[msgs.length - 1] = { ...last, thinking: `${last.thinking || ''}${textChunk}` };
        return msgs;
      });
    };
    const failTurn = (message, { alsoRemoveAssistant = false } = {}) => {
      if (!fg()) return;
      try { streamerRef.current?.flushNow(); } catch {}
      if (replayLastUser) {
        if (alsoRemoveAssistant) {
          setMessages((prev) => {
            const msgs = [...prev];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant' && !last.content) msgs.pop();
            return msgs;
          });
        }
      } else {
        setMessages((prev) => removeFailedTurn(prev, alsoRemoveAssistant));
        restoreInput(text);
        if (!convIdAtStart) setCurrentConvId(null);
      }
      setChatError(message);
    };

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/chat/stream', {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          conversation_id: convIdAtStart || undefined,
          message: text,
          file_ids: fileIds,
          drive_folders: driveFolderPaths,   // 드라이브 폴더 경로 — 서버가 하위 폴더까지 읽는다
          web_search: webSearch,
          reasoning: !!reasoning,
          model: selectedChatModel || undefined,
          project_id: convIdAtStart ? undefined : (projectIdAtStart || undefined),
          cowork: !!cowork && (convIdAtStart ? true : !!projectIdAtStart),
          internal_data: !!internalData,
          use_rag: !!useRag,
          skill_id: skillIdRef?.current ?? null,   // 스킬로 시작한 턴이면 절차·검증을 서버가 지침으로 넣는다
        }),
      });
      // 스킬은 그 한 턴에만 적용한다. 비우지 않으면 이후 모든 질문에 절차·검증이 계속 붙는다.
      if (skillIdRef) skillIdRef.current = null;

      // 백그라운드 생성이 켜져 있으면 서버가 run_id 를 헤더로 준다. 중지에 쓴다.
      const _rid = res.headers?.get?.('X-Chat-Run-Id') || null;   // 테스트 모의 응답엔 headers 가 없다
      if (_rid) runIdsRef.current.set(convIdAtStart ?? null, _rid);

      // 비-2xx: JSON 에러 파싱
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        if (res.status === 429 && detail?.code === 'LIMIT_EXCEEDED') {
          if (!replayLastUser) {
            setMessages((prev) => prev.slice(0, -1));
            restoreInput(text);
          }
          setLimitModal({ used: detail.used, limit: detail.limit });
          setLimitReason('');
          chatAPI.getLimitRequestStatus().then((r) => setLimitRequested(r.data.has_pending)).catch(() => setLimitRequested(false));
        } else if (res.status === 429 && detail?.code === 'WEB_SEARCH_LIMIT_EXCEEDED') {
          if (!replayLastUser) {
            setMessages((prev) => prev.slice(0, -1));
            restoreInput(text);
          }
          setWebLimitModal({ used: detail.used, limit: detail.limit });
          chatAPI.getLimitRequestStatus('web_search').then((r) => setWebLimitRequested(r.data.has_pending)).catch(() => setWebLimitRequested(false));
        } else if (res.status === 409 && detail?.code === 'RUN_IN_PROGRESS') {
          setBlockedConvId(convIdAtStart ?? null);
          failTurn(mapHttpError(res.status, detail));
        } else {
          if (detail?.code === 'ATTACHMENT_UNAVAILABLE') {
            removeAttachmentReuseFileIds(convIdAtStart, (detail.files || []).map((file) => file?.id));
          }
          failTurn(mapHttpError(res.status, detail));
        }
        return;
      }

      // 스트리밍 시작: 빈 assistant 메시지 추가
      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      assistantAdded = true;
      const streamer = createSmoothStreamer((content) => {
        if (!fg()) return;   // 백그라운드로 분리된 스트림은 화면(messages)을 건드리지 않음
        setMessages((prev) => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last && last.role === 'assistant') msgs[msgs.length - 1] = { ...last, content };
          return msgs;
        });
      });
      streamerRef.current = streamer;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop(); // 마지막 미완성 청크 보관

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let data;
          try { data = JSON.parse(part.slice(6)); } catch { continue; }

          if (data.type === 'meta') {
            // 대화 식별자만 연결(사이드바 추가는 첫 토큰 후 — 토큰 없이 끝난 빈 대화는 목록에 안 남김)
            streamConvId = data.conversation_id;   // UNgated: 스트림 자체 conv_id 추적은 항상
            // 새 대화는 전송 시점에 id 가 없어 run_id 를 null 키로 넣어 뒀다. 이제 진짜 id 로 옮긴다
            // (안 옮기면 중지 버튼이 run_id 를 못 찾아 서버가 안 멈춘다).
            if (_rid && streamConvId) runIdsRef.current.set(streamConvId, _rid);
            streamProjectId = data.project_id ?? projectIdAtStart ?? null;
            streamAccessRole = data.access_role ?? currentProjectAccessRole ?? null;
            markGen(data.conversation_id);   // 새 대화는 conv_id 알게 된 시점에 '작성 중' 표시
            if (fg()) {
              currentConvIdRef.current = data.conversation_id;
              setCurrentConvId(data.conversation_id);   // 포그라운드일 때만 보이는 대화 전환
            }
          } else if (data.type === 'sources') {
            // 문서 기반 답변(RAG): 참고한 사내 문서 출처 → 답변 아래 카드로 표시
            if (fg()) setRagSources({ convId: streamConvId || convIdAtStart, list: data.sources || [] });
          } else if (data.type === 'citation') {
            updateLastAssistantCitations(data, { isForeground: fg });
          } else if (data.type === 'token') {
            receivedToken = true;
            if (!convIdAtStart && streamConvId) {   // 첫 토큰 도착 시 새 대화를 사이드바에 1회 추가(meta의 conv_id 사용)
              const newId = streamConvId;
              const newProjectId = streamProjectId;
              const newAccessRole = streamAccessRole;
              setConversations((prev) => prev.some((c) => c.id === newId) ? prev : [
                {
                  id: newId,
                  title: text.slice(0, 50),
                  project_id: newProjectId ?? null,
                  access_role: newProjectId ? newAccessRole : null,
                  updated_at: new Date().toISOString(),
                },
                ...prev.filter((c) => c.id !== newId),
              ]);
            }
            streamer.push(data.text);   // rAF 타이핑으로 부드럽게 노출
          } else if (data.type === 'thinking') {
            appendThinking(data.text || '');
          } else if (data.type === 'error') {
            const message = data.message || '오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
            specificStreamErrorShown = true;
            if (fg()) showStreamLimit(data);
            if (receivedToken) {
              if (fg()) {
                streamer.flushNow();
                setChatError(message);
              }
            } else if (isStructuredTerminalError(data)) {
              failedBeforeToken = true;
              persistedTerminalBeforeToken = true;
              if (fg()) {
                if (assistantAdded) setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant' && !last.content) msgs.pop();
                  return msgs;
                });
                setChatError(message);
              }
            } else {
              failedBeforeToken = true;
              failTurn(message, { alsoRemoveAssistant: true });
            }
          } else if (data.type === 'status') {
            if (fg()) {
              if (data.phase === 'document') setGeneratingDoc(true);
              // PPT 는 5단계 파이프라인이라 5~10분 걸린다(실측 400~630초) — 문구를 다르게 줘야
              // 사용자가 멈춘 줄 알고 새로고침하지 않는다.
              else if (data.phase === 'presentation') {
                // 서버가 단계별로 status 를 흘린다(기획/테마/레이아웃/그림/렌더/검수/수정).
                // message 가 있으면 그 단계를 보여준다 — 없으면 화면이 5~10분간 안 움직여
                // 사용자가 멈춘 줄 알고 새로고침한다.
                setGeneratingDoc(data.message
                  ? `발표자료 만드는 중 — ${data.message} (전체 5~10분)`
                  : '발표자료 만드는 중 — 기획 → 디자인 → 그림 → 검수 (5~10분 걸립니다)');
              }
              // 서버는 tool_pending(도구 이름) 다음에 이 status 를 보낸다. 그냥 true 로 덮으면
              // 방금 세운 구체적 라벨('파일 읽는 중')이 '도구 사용 중'으로 되돌아간다.
              else if (data.phase === 'workspace') setWorkspaceBusy((prev) => (typeof prev === 'string' ? prev : true));
            }
          } else if (data.type === 'state') {
            // 도구 호출이 시작될 때 서버가 tool_pending(+tool_name)을 보낸다.
            // 무엇을 하는 중인지 보여준다. workspaceBusy 는 boolean 또는 라벨 문자열이고
            // 둘 다 truthy 라 기존 표시 동작은 그대로다.
            if (fg() && data.state === 'tool_pending' && data.tool_name) {
              setWorkspaceBusy(TOOL_LABELS[data.tool_name] || '도구 사용 중');
            }
          } else if (data.type === 'workspace_file') {
            // 작업공간 파일 생성/수정됨: 패널 갱신(포그라운드일 때만)
            if (fg()) {
              setWorkspaceBusy(false);
              if (projectIdAtStart) loadWorkspaceFiles(projectIdAtStart);
            }
          } else if (data.type === 'file') {
            // 생성된 문서: 마커는 완료 시 한 번에 붙여 다운로드 카드 표시(부분 노출 방지)
            if (fg()) setGeneratingDoc(false);
            streamer.addSuffix(`\n\n${generatedFileMarker(data)}`);   // 게이트된 콜백 경유=백그라운드면 화면 미반영
          } else if (data.type === 'document_error') {
            const message = data.message || '문서 생성 중 오류가 발생했습니다.';
            receivedToken = true;
            if (fg()) {
              setGeneratingDoc(false);
              setChatError(message);
            }
          } else if (data.type === 'done') {
            const terminalWithoutToken = !receivedToken && terminalStatuses.has(data.status);
            const terminalWithToken = receivedToken && terminalStatuses.has(data.status);
            if (failedBeforeToken || terminalWithoutToken) {
              streamer.finish();
              if (terminalWithoutToken && fg() && !specificStreamErrorShown) {
                setChatError(terminalStatusMessage(data));
              }
              if (fg() && assistantAdded) setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant' && !last.content) msgs.pop();
                return msgs;
              });
              if ((persistedTerminalBeforeToken || terminalWithoutToken) && data.conversation_id) {
                if (fg()) {
                  currentConvIdRef.current = data.conversation_id;
                  setCurrentConvId(data.conversation_id);
                }
                if (currentConvIdRef.current === data.conversation_id) {
                  historyAPI.getMessages(data.conversation_id)
                    .then((r) => {
                      if (currentConvIdRef.current === data.conversation_id) {
                        replaceMessagesFromHistory(setMessages, r.data, data.conversation_id);
                      }
                    })
                    .catch(() => {});
                }
              }
              historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
              refreshMyUsage();
              unmarkGen(data.conversation_id);
              continue;
            }
            if (terminalWithToken && fg() && !specificStreamErrorShown) {
              streamer.flushNow();
              setChatError(terminalStatusMessage(data));
            }
            if (fg()) {
              setGeneratingDoc(false);
              setWorkspaceBusy(false);
              currentConvIdRef.current = data.conversation_id;
              setCurrentConvId(data.conversation_id);   // 백그라운드 스트림은 사용자 현재 뷰를 끌어오면 안 됨(중요)
            }
            streamer.finish();   // 게이트된 콜백 경유 + 자체 rAF 종료
            // 어시스턴트 답변 시각 표시: 완료된 메시지에 created_at 주입(없을 때만). 히스토리 재로딩 시 DB값으로 대체됨.
            if (fg()) setMessages((prev) => {
              const a = [...prev];
              const last = a[a.length - 1];
              if (last && last.role === 'assistant' && !last.created_at) a[a.length - 1] = {
                ...last,
                citations: data.citations?.length ? mergeCitations([], data.citations) : (last.citations || []),
                created_at: new Date().toISOString(),
              };
              return a;
            });
            historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});   // 백그라운드여도 사이드바 반영
            refreshMyUsage();
            if (fg() && cowork && projectIdAtStart) loadWorkspaceFiles(projectIdAtStart);
            // 백그라운드 완료 시 마침 그 대화를 보고 있으면 전체 답변으로 갱신(부분→완성본)
            if (currentConvIdRef.current === data.conversation_id) {
              const doneThinkingText = thinkingText;
              historyAPI.getMessages(data.conversation_id)
                .then((r) => {
                  if (currentConvIdRef.current === data.conversation_id) {
                    replaceMessagesFromHistory(setMessages, r.data, data.conversation_id, (loaded) => withEphemeralThinking(loaded, doneThinkingText));
                  }
                })
                .catch(() => {});
            }
            unmarkGen(data.conversation_id);   // 완료 즉시 '작성 중' 해제(markGen과 동일 id 소스 — 새 대화 잔류 방지)
          }
        }
      }
      streamer.finish();   // 스트림이 done 이벤트 없이 닫혀도 잔여 텍스트 마무리
    } catch (e) {
      if (fg() && streamerRef.current) streamerRef.current.flushNow();   // 포그라운드일 때만 부분 응답 전량 표시
      if (e.name === 'AbortError') {
        // 사용자가 응답을 중지함: 받은 부분 응답은 그대로 유지
        const abortedConvId = streamConvId || convIdAtStart;
        if (!receivedToken && fg()) {
          if (abortedConvId) {
            if (assistantAdded) setMessages(removeTrailingEmptyAssistant);
            currentConvIdRef.current = abortedConvId;
            setCurrentConvId(abortedConvId);
            historyAPI.getMessages(abortedConvId)
              .then((r) => {
                if (currentConvIdRef.current === abortedConvId) {
                  replaceMessagesFromHistory(setMessages, r.data, abortedConvId);
                }
              })
              .catch(() => {});
          } else if (!replayLastUser) {
            setMessages((prev) => removeFailedTurn(prev, assistantAdded));
            restoreInput(text);
            setCurrentConvId(null);
          }
        }
        historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
        refreshMyUsage();
      } else {
        const message = '연결이 끊겼습니다. 네트워크를 확인하고 다시 시도해 주세요.';
        const droppedConvId = streamConvId || convIdAtStart;   // 중지 경로(:735)와 같은 식
        if (receivedToken) {
          if (fg()) setChatError(message);
        } else if (assistantAdded && droppedConvId && fg()) {
          // 폰에서 앱을 나가면 브라우저가 페이지를 정지시켜 fetch가 끊긴다. 예전엔 여기서
          // 턴을 지우고 새 대화면 setCurrentConvId(null)까지 해서 초기 화면으로 튕겼다 —
          // 서버엔 질문이 멀쩡히 있는데 화면에서만 사라진 것이다.
          // 🔴 판단 기준은 assistantAdded(=HTTP 200) 다. chat/core.py 는 유저 메시지를
          //    db.commit() 한 뒤에야 StreamingResponse 를 돌려주고, generate() 는 제너레이터라
          //    그 시점엔 한 줄도 안 돈다. 즉 **200 을 받았으면 질문은 이미 저장돼 있다** —
          //    meta 수신을 기다리면 200~meta 사이에 끊긴 경우를 놓쳐서 질문이 사라진다.
          setMessages(removeTrailingEmptyAssistant);
          currentConvIdRef.current = droppedConvId;
          setCurrentConvId(droppedConvId);
          historyAPI.getMessages(droppedConvId)
            .then((r) => {
              // 🔴 fg() 를 반드시 함께 본다. 이 요청엔 타임아웃이 없어서(api/client.js) 몇 분 뒤에도 온다.
              //    그 사이 사용자가 같은 대화에 다시 물었으면 myToken 이 올라가 fg() 가 false 다.
              //    안 보면 재전송한 질문과 생성 중인 답변을 옛 스냅샷으로 덮어쓴다.
              if (fg() && currentConvIdRef.current === droppedConvId) {
                replaceMessagesFromHistory(setMessages, r.data, droppedConvId);
              }
            })
            .catch(() => {
              // 재조회까지 실패 = 아직 끊긴 상태. 화면엔 질문 말풍선뿐이라 다시 보낼 수단이 없다
              // (재생성 버튼은 assistant 메시지가, 편집 버튼은 메시지 id 가 있어야 뜬다).
              // 그러니 글을 돌려준다 — 같은 텍스트로 재전송하면 서버가 reuse_last_user 로 같은 행을 재사용한다.
              if (fg()) restoreInput(text);
            });
          historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
          refreshMyUsage();
          setChatError(message);
        } else {
          // 200 을 못 받았거나(서버에 아무것도 없음) 붙들 대화 id 를 모른다 → 지금대로 글을 돌려준다.
          // 단 200 은 받았다면 서버엔 대화가 생겼으므로 사이드바만이라도 갱신한다(새로고침 전엔 안 보인다).
          if (assistantAdded) historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
          failTurn(message, { alsoRemoveAssistant: assistantAdded });
        }
      }
    } finally {
      unmarkGen(streamConvId || convIdAtStart);   // 스트림 종료 시 무조건 '작성 중' 해제(포그라운드 무관)
      if (fg()) {   // 내가 아직 포그라운드일 때만 전역 상태 정리(백그라운드 스트림의 늦은 정리가 새 스트림 덮어쓰는 것 방지)
        abortRef.current = null;
        setLoading(false);
        setGeneratingDoc(false);
        setWorkspaceBusy(false);
      }
    }
  };

  // ── 응답 재생성 ──
  const handleRegenerate = async () => {
    if (loading || !currentConvId) return;
    if (streamerRef.current) { streamerRef.current.flushNow(); streamerRef.current = null; }
    setChatError(null);
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const myToken = ++viewTokenRef.current;
    const fg = () => viewTokenRef.current === myToken;
    markGen(currentConvId);
    const previousAssistant = [...messages].reverse().find((m) => m.role === 'assistant') || null;
    let assistantAdded = false;
    let receivedToken = false;
    let failedBeforeToken = false;
    let specificStreamErrorShown = false;
    const restorePreviousAssistant = () => {
      if (!fg() || !previousAssistant) return;
      setMessages((prev) => {
        if (prev.some((m) => m === previousAssistant)) return prev;
        const msgs = [...prev];
        const last = msgs[msgs.length - 1];
        if (last?.role === 'assistant' && !last.content) msgs.pop();
        return [...msgs, previousAssistant];
      });
    };

    // UI에서 마지막 assistant 메시지 제거
    setMessages((prev) => {
      const msgs = [...prev];
      for (let i = msgs.length - 1; i >= 0; i--) {
        if (msgs[i].role === 'assistant') { msgs.splice(i, 1); break; }
      }
      return msgs;
    });

    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/chat/regenerate', {
        method: 'POST',
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ conversation_id: currentConvId }),
      });

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const detail = errData.detail;
        if (res.status === 429 && detail?.code === 'LIMIT_EXCEEDED') {
          setLimitModal({ used: detail.used, limit: detail.limit });
          setLimitReason('');
          chatAPI.getLimitRequestStatus().then((r) => setLimitRequested(r.data.has_pending)).catch(() => {});
        } else {
          setChatError(mapHttpError(res.status, detail));
        }
        restorePreviousAssistant();
        return;
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: '' }]);
      assistantAdded = true;
      const streamer = createSmoothStreamer((content) => {
        if (!fg()) return;   // 백그라운드로 분리된 스트림은 화면(messages)을 건드리지 않음
        setMessages((prev) => {
          const msgs = [...prev];
          const last = msgs[msgs.length - 1];
          if (last && last.role === 'assistant') msgs[msgs.length - 1] = { ...last, content };
          return msgs;
        });
      });
      streamerRef.current = streamer;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();

        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          let data;
          try { data = JSON.parse(part.slice(6)); } catch { continue; }

          if (data.type === 'token') {
            receivedToken = true;
            if (fg()) streamer.push(data.text);
          } else if (data.type === 'sources') {
            if (fg()) setRagSources({ convId: currentConvId, list: data.sources || [] });
          } else if (data.type === 'citation') {
            updateLastAssistantCitations(data, { isForeground: fg });
          } else if (data.type === 'error') {
            const message = data.message || '오류가 발생했습니다. 잠시 후 다시 시도해 주세요.';
            specificStreamErrorShown = true;
            if (fg()) showStreamLimit(data);
            if (receivedToken) {
              if (fg()) {
                streamer.flushNow();
                setChatError(message);
              }
            } else if (isStructuredTerminalError(data)) {
              failedBeforeToken = true;
              if (fg()) {
                if (assistantAdded) setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant' && !last.content) msgs.pop();
                  return msgs;
                });
                setChatError(message);
              }
              restorePreviousAssistant();
            } else {
              failedBeforeToken = true;
              if (fg()) {
                if (assistantAdded) setMessages((prev) => {
                  const msgs = [...prev];
                  const last = msgs[msgs.length - 1];
                  if (last?.role === 'assistant' && !last.content) msgs.pop();
                  return msgs;
                });
                setChatError(message);
              }
              restorePreviousAssistant();
            }
          } else if (data.type === 'done') {
            const terminalWithoutToken = !receivedToken && terminalStatuses.has(data.status);
            const terminalWithToken = receivedToken && terminalStatuses.has(data.status);
            if (failedBeforeToken || terminalWithoutToken) {
              if (fg()) streamer.finish();
              if (terminalWithoutToken && fg() && !specificStreamErrorShown) {
                setChatError(terminalStatusMessage(data));
              }
              if (fg() && assistantAdded) setMessages((prev) => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (last?.role === 'assistant' && !last.content) msgs.pop();
                return msgs;
              });
              restorePreviousAssistant();
              if (currentConvIdRef.current === currentConvId) {
                historyAPI.getMessages(currentConvId)
                  .then((r) => {
                    if (currentConvIdRef.current === currentConvId) {
                      replaceMessagesFromHistory(setMessages, r.data, currentConvId);
                    }
                  })
                  .catch(() => {});
              }
              refreshMyUsage();
              continue;
            }
            if (terminalWithToken && fg() && !specificStreamErrorShown) {
              streamer.flushNow();
              setChatError(terminalStatusMessage(data));
            }
            if (fg()) streamer.finish();
            if (fg() && data.citations?.length) {
              updateLastAssistantCitations(data.citations, { replace: true, isForeground: fg });
            }
            // 다른 대화 보고 있으면 messages 직접갱신 금지, 같은 대화면 서버 확정본 리로드
            if (currentConvIdRef.current === currentConvId) {
              historyAPI.getMessages(currentConvId)
                .then((r) => {
                  if (currentConvIdRef.current === currentConvId) {
                    replaceMessagesFromHistory(setMessages, r.data, currentConvId);
                  }
                })
                .catch(() => {});
            }
            refreshMyUsage();   // 사용량은 항상
          }
        }
      }
      streamer.finish();
    } catch (e) {
      if (fg() && streamerRef.current) streamerRef.current.flushNow();
      if (e.name === 'AbortError') {
        if (!receivedToken && fg()) {
          if (assistantAdded) setMessages(removeTrailingEmptyAssistant);
          restorePreviousAssistant();
          if (currentConvIdRef.current === currentConvId) {
            historyAPI.getMessages(currentConvId)
              .then((r) => {
                if (currentConvIdRef.current === currentConvId) {
                  replaceMessagesFromHistory(setMessages, r.data, currentConvId);
                }
              })
              .catch(() => {});
          }
        }
        refreshMyUsage();   // 사용량은 항상
      } else if (fg()) {
        const message = '연결이 끊겼습니다. 네트워크를 확인하고 다시 시도해 주세요.';
        if (receivedToken) {
          setChatError(message);
        } else {
          if (assistantAdded) setMessages((prev) => {
            const msgs = [...prev];
            const last = msgs[msgs.length - 1];
            if (last?.role === 'assistant' && !last.content) msgs.pop();
            return msgs;
          });
          setChatError(message);
          restorePreviousAssistant();
          // 중지(AbortError) 경로와 같이 서버 기록으로 맞춘다 — 재생성이 중간에 끊겨도
          // 서버가 남긴 상태(이전 답변 유지·재생성 시도 기록)를 화면이 따라가게 한다.
          if (currentConvIdRef.current === currentConvId) {
            historyAPI.getMessages(currentConvId)
              .then((r) => {
                // 🔴 fg() 필수 — 이 요청엔 타임아웃이 없어서 늦게 도착할 수 있고,
                //    그 사이 새 스트림이 시작됐다면 옛 스냅샷으로 화면을 덮어쓴다.
                if (fg() && currentConvIdRef.current === currentConvId) {
                  replaceMessagesFromHistory(setMessages, r.data, currentConvId);
                }
              })
              .catch(() => {});
          }
          refreshMyUsage();   // 중지 분기(:989 위)와 대칭
        }
      }
    } finally {
      unmarkGen(currentConvId);   // 재생성 종료 시 무조건 '작성 중' 해제
      if (fg()) {
        abortRef.current = null;
        setLoading(false);
      }
    }
  };

  const handleEditMessage = async (messageId, content) => {
    if (loading || (currentConvId && genConvIds.has(currentConvId)) || !currentConvId) return false;
    const target = messages.find((m) => m.id === messageId);
    if (!target || target.role !== 'user') return false;
    const hasFiles = Array.isArray(target.files) && target.files.length > 0;
    const nextContent = (content || '').trim() || (hasFiles ? '(첨부 파일)' : '');
    if (!nextContent) return false;
    if (!(await confirmDialog('이후 대화가 삭제되고 다시 생성됩니다. 계속하시겠습니까?', { danger: true }))) return false;

    try {
      await chatAPI.editMessage(messageId, { content: nextContent });
      setRagSources(null);
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx < 0) return prev;
        return [...prev.slice(0, idx), { ...prev[idx], content: nextContent }];
      });
      await handleSend({ replayLastUser: true, conversationId: currentConvId, message: nextContent });
      historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
      return true;
    } catch (e) {
      setChatError(mapHttpError(e.response?.status, e.response?.data?.detail));
      return false;
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (loading || (currentConvId && genConvIds.has(currentConvId))) return false;
    const target = messages.find((m) => m.id === messageId);
    if (!target || target.role !== 'user') return false;
    if (!(await confirmDialog('이 메시지 이후 대화가 삭제됩니다. 계속하시겠습니까?', { danger: true }))) return false;

    try {
      await chatAPI.deleteMessage(messageId);
      setRagSources(null);
      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        return idx < 0 ? prev : prev.slice(0, idx);
      });
      historyAPI.listConversations().then((r) => setConversations(r.data)).catch(() => {});
      return true;
    } catch (e) {
      setChatError(mapHttpError(e.response?.status, e.response?.data?.detail));
      return false;
    }
  };

  handleSendRef.current = handleSend;

  return {
    input, setInput, textareaRef, loading, generatingDoc, genConvIds,
    blockedConvId, stopBlockingRunAndRetry,
    detachStream, stopResponse, handleSend, handleRegenerate, handleEditMessage, handleDeleteMessage,
  };
}
